import React, { useState } from 'react'
import { FaKey, FaMobileAlt } from 'react-icons/fa'
import { Box, Stack, Heading, Text, Button, Icon, SimpleGrid } from '@/components/lib'
import EntryContainer from '@/components/includes/EntryContainer'
import AuthenticatorSetup from './AuthenticatorSetup'
import PasskeyRegistration from './PasskeyRegistration'
import RecoveryCodes from './RecoveryCodes'
import utils from '../../utils/utils'
import api from '../../api'
import useStore from '../../store'

// The screen every password account sees, in place of the app, until it has
// a second factor. App.jsx shows it whenever /users/me reports
// mfaEnrolRequired - straight after login, and on the next visit of a session
// that predates the requirement.
//
// A login that arrives here holds a restricted token the API confines to the
// routes these two set-ups use. Finishing either hands back a full token,
// which completeLogin swaps in; an older full session gets no new token and
// just refreshes the user.
export default function SetupMfa () {
  const [method, setMethod] = useState(null)
  // The enrolment response, held while the recovery codes it carries are on
  // screen. The session isn't completed until the user says they've saved
  // them, because this is the one time the codes are readable.
  const [pending, setPending] = useState(null)
  const { user, completeLogin, updateUser, completeLogout } = useStore()

  function finished (data) {
    utils.toast('success', 'Two-factor authentication is set up')
    if (data?.recoveryCodes?.length) {
      setPending(data)
    } else {
      enterApp(data)
    }
  }

  function enterApp (data) {
    if (data?.token) {
      completeLogin(data.token)
    } else {
      updateUser(user._id, {
        mfaEnrolRequired: false,
        otpEnabled: !!data?.otpEnabled || user.otpEnabled,
        hasPasskey: !!data?.passkey || user.hasPasskey,
        recoveryCodesStatus: data?.recoveryCodes ? { remaining: data.recoveryCodes.length, used: 0, generatedAt: new Date().toISOString() } : user.recoveryCodesStatus
      })
    }
  }

  function logout () {
    api.accounts.logout(() => completeLogout(), () => api.accounts.localLogout(() => completeLogout()))
  }

  if (pending) {
    return (
      <EntryContainer>
        <Stack gap={5} maxW='640px'>
          <Box>
            <Heading size='lg'>Save your recovery codes</Heading>
            <Text mt={2}>Two-factor authentication is set up. Before you carry on, keep these recovery codes somewhere safe because they can help you recover your account.</Text>
          </Box>
          <RecoveryCodes codes={pending.recoveryCodes} />
          <Box>
            <Button colorPalette='primary' onClick={() => enterApp(pending)}>I've saved my recovery codes</Button>
          </Box>
        </Stack>
      </EntryContainer>
    )
  }

  return (
    <EntryContainer>
      <Stack gap={5} maxW='640px'>
        <Box>
          <Heading size='lg'>Set up two-factor authentication</Heading>
          <Text mt={2}>
            Trialflare now requires every account with a password to have a second factor as well.
            It only takes a minute, and you'll be asked for it each time you sign in with your password.
          </Text>
        </Box>

        {!method &&
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
            <Stack rounded='lg' borderWidth='1px' p={4} gap={3} bg='bg.subtle'>
              <Stack direction='row' alignItems='center' gap={3}>
                <Icon as={FaKey} boxSize={6} />
                <Heading size='md' as='h3'>Passkey</Heading>
              </Stack>
              <Text flex={1}>Use your device's fingerprint, face recognition or screen lock, or a hardware security key. The quickest option, and you can later sign in with it alone.</Text>
              <Box><Button colorPalette='primary' onClick={() => setMethod('passkey')}>Use a passkey</Button></Box>
            </Stack>
            <Stack rounded='lg' borderWidth='1px' p={4} gap={3} bg='bg.subtle'>
              <Stack direction='row' alignItems='center' gap={3}>
                <Icon as={FaMobileAlt} boxSize={6} />
                <Heading size='md' as='h3'>Authenticator app</Heading>
              </Stack>
              <Text flex={1}>Use a six-digit code from an app such as Google Authenticator, Microsoft Authenticator or Bitwarden each time you sign in.</Text>
              <Box><Button colorPalette='primary' onClick={() => setMethod('otp')}>Use an authenticator app</Button></Box>
            </Stack>
          </SimpleGrid>}

        {method === 'passkey' &&
          <Stack rounded='lg' borderWidth='1px' p={4} gap={3} bg='bg.subtle'>
            <Heading size='md' as='h3'>Add a passkey</Heading>
            <PasskeyRegistration onComplete={finished} />
          </Stack>}

        {method === 'otp' &&
          <Stack rounded='lg' borderWidth='1px' p={4} gap={3} bg='bg.subtle'>
            <Heading size='md' as='h3'>Set up an authenticator app</Heading>
            <AuthenticatorSetup onComplete={finished} />
          </Stack>}

        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          {method
            ? <Button variant='ghost' onClick={() => setMethod(null)}>Choose a different method</Button>
            : <Box />}
          <Button variant='ghost' onClick={logout}>Sign out</Button>
        </Stack>
      </Stack>
    </EntryContainer>
  )
}
